import { ChangeEvent, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { BaseModal, BaseModalProps } from '../BaseModal';
import { ColumnBox, RowBox } from '../../../styles/commonStyles';
import { useModal } from '../../../contexts/ModalContext';
import { CardLayout } from '../../layouts/CardLayout';
import { TextField } from '../../common/TextField';
import { SearchSelect } from '../../common/SearchSelect';
import { RadioButtonGroup } from '../../common/RadioButtonGroup';
import ThemeButton from '../../common/ThemeButton';
import { theme } from '../../../configs/theme';
import { InputsLayout } from '../../layouts/InputsLayout';
import { AddContactsToProjectRequest } from '../../../types/backendRequests/AddContactsToProjectRequest';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import { ThemeTypography } from '../../common/ThemeTypography';
import { Contact } from '../../../types/backendResponses/Contact';
import { AddExistingContactsToProjectRequest } from '../../../types/backendRequests/AddExistingContactsToProjectRequest';
import { useGetContactsByCustomerId } from '../../../api/ContactService/useGetContactsByCustomerId';
import { useGetProject } from '../../../api/ProjectService/useGetProject';
import { useAddContactsToProject } from '../../../api/ProjectService/useAddContactsToProject';
import { useAddExistingContactsToProject } from '../../../api/ProjectService/useAddExistingContactsToProject';
import { useAlert } from '../../../contexts/AlertContext';

export default function ProjectContactModal({ name }: BaseModalProps) {
  const { modalState, close, setModalUpdated } = useModal();
  const { alert } = useAlert();

  const projectId = modalState?.ProjectContactModal?.payload?.projectId;
  const customerId = modalState?.ProjectContactModal?.payload?.customerId;

  const initialContactState: Contact = {
    name: '',
    email: '',
    phone1: '',
    phone2: '',
    id: '',
  };

  const [projectContacts, setProjectContacts] = useState<Contact[]>([]);
  const [newContact, setNewContact] = useState<Contact>(initialContactState);
  const { action: addContactsToProject, isLoading: isAddingToProject } = useAddContactsToProject();
  const { action: addExistingContactsToProject, isLoading: isAddingExistingToProject } =
    useAddExistingContactsToProject();
  const { action: getProject, data: project } = useGetProject();
  const { action: getContacts, data: customersContacts } = useGetContactsByCustomerId();

  useEffect(() => {
    if (customerId) {
      getContacts(customerId);
    }
    if (projectId) {
      getProject(projectId);
    }
  }, [customerId, projectId]);

  const [contactType, setContactType] = useState<'new' | 'existing'>('new');

  useEffect(() => {
    if (!project?.contacts) {
      return;
    }
    setProjectContacts(project.contacts);
    // Chosen once the project has loaded; at mount it is not there yet, so this always gave 'new'
    setContactType(project.contacts.length > 0 ? 'existing' : 'new');
  }, [project]);

  // A new contact belongs to the project's customer
  const projectCustomerId = customerId ?? project?.customer?.id;

  const handleContactTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setContactType(event.target.value as 'new' | 'existing');
  };

  const handleFormUpdate = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, ...{ [name]: value } }));
  };

  const handleFormExistingContactsUpdate = (index: number, selectedContact: Contact | null) => {
    if (!selectedContact) {
      return;
    }

    setProjectContacts((prev) => {
      const updated = [...prev];
      updated[index] = selectedContact;
      return updated;
    });
  };

  // Get available contacts for a specific dropdown, excluding already selected contacts
  const getAvailableContacts = (currentIndex: number): Contact[] => {
    if (!customersContacts) {
      return [];
    }

    const selectedContactIds = projectContacts
      .map((contact, index) => (index !== currentIndex && contact.id ? contact.id : null))
      .filter((id): id is string => id !== null && id !== '');

    return customersContacts.filter((contact) => !selectedContactIds.includes(contact.id ?? ''));
  };

  // Check if all existing contacts have been selected (have valid IDs)
  const hasValidContacts = (): boolean => {
    return projectContacts.every((contact) => contact.id && contact.id.length > 0);
  };

  // Check if there are any available contacts left to add
  const hasAvailableContactsToAdd = (): boolean => {
    const selectedContactIds = projectContacts
      .map((contact) => contact.id)
      .filter((id): id is string => id !== null && id !== '' && id !== undefined);

    const availableContacts = customersContacts?.filter(
      (contact) => !selectedContactIds.includes(contact.id ?? '')
    );

    return (availableContacts?.length ?? 0) > 0;
  };

  const submitNewContacts = async () => {
    // Filter out any empty contacts before submitting
    const validContacts = projectContacts.filter((contact) => contact.id && contact.id.length > 0);

    const addContactsToProjectRequest: AddExistingContactsToProjectRequest = {
      contacts: validContacts,
      projectId: projectId,
    };
    const success = await addExistingContactsToProject(addContactsToProjectRequest);
    if (!success) {
      alert('error', 'Kunde inte spara kontaktpersonerna. Försök igen senare.');
      return;
    }
    alert('success', 'Kontaktpersoner sparade');
    setModalUpdated(true);
    close('ProjectContactModal');
  };

  const submitNewContact = async () => {
    if (!projectCustomerId) {
      return;
    }

    const addContactsToProjectRequest: AddContactsToProjectRequest = {
      contacts: [newContact],
      customerId: projectCustomerId,
      projectId: projectId,
    };
    const success = await addContactsToProject(addContactsToProjectRequest);
    if (!success) {
      alert('error', 'Kunde inte skapa kontaktpersonen. Försök igen senare.');
      return;
    }
    alert('success', 'Kontaktperson tillagd');
    setModalUpdated(true);
    close('ProjectContactModal');
  };

  const addRow = () => {
    setProjectContacts((prev) => [...prev, initialContactState]);
  };

  const removeRow = (contact: Contact, index: number) => {
    const newList = projectContacts.toSpliced(index, 1);
    setProjectContacts(newList);
  };

  return (
    <BaseModal
      name={name}
      title={
        contactType === 'existing' ? 'Koppla kontaktpersoner' : 'Skapa och koppla ny kontaktperson'
      }
    >
      <InputsLayout>
        <RadioButtonGroup
          radioButtons={[
            { title: 'Befintlig kontaktperson', value: 'existing' },
            { title: 'Ny kontaktperson', value: 'new' },
          ]}
          groupValue={contactType}
          onChange={handleContactTypeChange}
          name="contactType"
        />
      </InputsLayout>
      {contactType === 'existing' && (
        <>
          <CardLayout title="Kontaktperson">
            <Box sx={{ padding: '20px', gap: '0.8rem', display: 'flex', flexDirection: 'column' }}>
              <ThemeTypography type="body2" sx={{ margin: 0 }}>
                När du sparar ersätts de kopplade kontaktpersonerna med listan nedan.
              </ThemeTypography>
              <ThemeTypography type="caption">Kontakt</ThemeTypography>
              {projectContacts?.map((contact, index) => (
                <RowBox
                  key={contact.id?.length ? contact.id + contact.email : index}
                  sx={{ alignItems: 'center', gap: '2.5rem' }}
                >
                  <SearchSelect<Contact>
                    name={`contact-${index}`}
                    options={getAvailableContacts(index)}
                    onChange={(e) => handleFormExistingContactsUpdate(index, e.target.value)}
                    getOptionLabel={(contact) =>
                      typeof contact === 'object' && contact !== null ? contact.name : ''
                    }
                    getOptionKey={(contact) =>
                      typeof contact === 'object' && contact !== null ? contact.id : contact
                    }
                    value={projectContacts[index]} // Bind the correct value
                  />
                  <IconButton onClick={() => removeRow(contact, index)}>
                    <Delete />
                  </IconButton>
                </RowBox>
              ))}

              {projectContacts.length === 0 && (
                <ThemeTypography type="italic" sx={{ margin: 0 }}>
                  {(customersContacts?.length ?? 0) === 0
                    ? 'Kunden har inga kontaktpersoner än. Välj Ny kontaktperson för att skapa en.'
                    : 'Inga kontaktpersoner kopplade. Lägg till med +.'}
                </ThemeTypography>
              )}

              <ThemeButton
                onClick={() => addRow()}
                isDisabled={!hasValidContacts() || !hasAvailableContactsToAdd()}
              >
                <Add />
              </ThemeButton>
            </Box>
          </CardLayout>
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row-reverse',
              padding: '0.5rem',
              position: 'sticky',
              bottom: 0,
              backgroundColor: theme.palette.customColors.cultured,
            }}
          >
            <ThemeButton
              onClick={() => submitNewContacts()}
              isDisabled={isAddingExistingToProject || !hasValidContacts()}
            >
              Spara
            </ThemeButton>
          </Box>
        </>
      )}
      {contactType === 'new' && (
        <RowBox>
          <ColumnBox sx={{ width: '100%', gap: '1rem' }}>
            <CardLayout title="Allmänna uppgifter" index={1}>
              <InputsLayout>
                {/* The contact is created on the project's customer; choosing another one did nothing */}
                <TextField
                  name="customer"
                  value={project?.customer?.name ?? ''}
                  readOnly
                  disabled
                  wrapperProps={{
                    label: 'Kund',
                    sx: { padding: '20px', width: '100%' },
                  }}
                />
              </InputsLayout>

              <InputsLayout>
                <TextField
                  name="name"
                  required
                  value={newContact.name}
                  onChange={handleFormUpdate}
                  wrapperProps={{
                    sx: { padding: '20px', width: '100%' },
                    label: 'Fullständigt namn',
                  }}
                />
              </InputsLayout>

              <InputsLayout>
                <TextField
                  name="email"
                  value={newContact.email}
                  onChange={handleFormUpdate}
                  wrapperProps={{
                    sx: { padding: '20px', width: '100%' },
                    label: 'Email',
                  }}
                />
              </InputsLayout>
              <InputsLayout>
                <TextField
                  name="phone1"
                  value={newContact.phone1}
                  onChange={handleFormUpdate}
                  wrapperProps={{
                    sx: { padding: '20px', width: '100%' },
                    label: 'Telefon 1',
                  }}
                />

                <TextField
                  name="phone2"
                  value={newContact.phone2}
                  onChange={handleFormUpdate}
                  wrapperProps={{
                    sx: { padding: '20px', width: '100%' },
                    label: 'Telefon 2',
                  }}
                />
              </InputsLayout>
            </CardLayout>
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'row-reverse',
                padding: '0.5rem',
                position: 'sticky',
                bottom: 0,
                backgroundColor: theme.palette.customColors.cultured,
              }}
            >
              <ThemeButton
                onClick={submitNewContact}
                isDisabled={isAddingToProject || !projectCustomerId || !newContact.name.trim()}
              >
                Spara
              </ThemeButton>
            </Box>
          </ColumnBox>
        </RowBox>
      )}
    </BaseModal>
  );
}
